import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CalendarToday,
  AccessTime,
  LocationOn,
  Person,
  CheckCircle,
  Cancel,
  Pending,
  Warning
} from '@mui/icons-material';
import dayjs from 'dayjs';
import axios from '../../config/axios';
import ModalConfirm from '../../components/ModalConfirm';
import ModalResult from '../../components/ModalResult';

const ReservationCards = ({ reservations, onRefresh }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [resultTitle, setResultTitle] = useState('');
  const [resultMessage, setResultMessage] = useState('');
  const [resultSeverity, setResultSeverity] = useState('info');
  const [actionType, setActionType] = useState('');
  const [reservationId, setReservationId] = useState(null);

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
      cancelled: 'default',
      completed: 'info'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'In attesa',
      approved: 'Approvata',
      rejected: 'Rifiutata',
      cancelled: 'Annullata',
      completed: 'Completata'
    };
    return labels[status] || status;
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      paid: 'success',
      refunded: 'info',
      failed: 'error'
    };
    return colors[status] || 'default';
  };

  const getPaymentStatusLabel = (status) => {
    const labels = {
      pending: 'In attesa',
      paid: 'Pagata',
      refunded: 'Rimborsata',
      failed: 'Fallita'
    };
    return labels[status] || status;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle />;
      case 'rejected':
      case 'cancelled':
        return <Cancel />;
      case 'pending':
        return <Pending />;
      case 'completed':
        return <CheckCircle />;
      default:
        return <Warning />;
    }
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes}min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  const getDayName = (dayOfWeek) => {
    const days = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
    return days[dayOfWeek];
  };

  const handleApprove = (reservationId) => {
    setActionType('approve');
    setReservationId(reservationId);
    setConfirmOpen(true);
  };

  const handleReject = (reservationId) => {
    setActionType('reject');
    setReservationId(reservationId);
    setConfirmOpen(true);
  };

  const handleConfirmAction = async () => {
    setConfirmOpen(false);
    
    try {
      if (actionType === 'approve') {
        await axios.put(`/api/reservations/${reservationId}/approve`);
        setResultTitle('Prenotazione Approvata');
        setResultMessage('La prenotazione è stata approvata con successo.');
        setResultSeverity('success');
      } else if (actionType === 'reject') {
        await axios.put(`/api/reservations/${reservationId}/reject`, {
          reason: 'Rifiutata dall\'amministratore'
        });
        setResultTitle('Prenotazione Rifiutata');
        setResultMessage('La prenotazione è stata rifiutata.');
        setResultSeverity('warning');
      }
      
      setResultOpen(true);
      onRefresh(); // Refresh the list
    } catch (err) {
      console.error(`Error ${actionType}ing reservation:`, err);
      setResultTitle('Errore');
      setResultMessage(`Si è verificato un errore durante l'${actionType === 'approve' ? 'approvazione' : 'rifiuto'} della prenotazione. Riprova più tardi.`);
      setResultSeverity('error');
      setResultOpen(true);
    }
    
    setActionType('');
    setReservationId(null);
  };

  const handleCancelAction = () => {
    setConfirmOpen(false);
    setActionType('');
    setReservationId(null);
  };

  const handleResultClose = () => {
    setResultOpen(false);
  };

  if (reservations.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CalendarToday sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Nessuna prenotazione trovata
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Modifica i filtri per vedere più risultati
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={3} alignItems="stretch">
      {reservations.map((reservation) => (
        <Grid  size={{ xs: 12, sm: 6, md: 4 }} key={reservation._id}>
          <Card sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column'
          }}>
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              {/* Header with status and price */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {reservation.reservable?.name || 'Risorsa non disponibile'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Stato:
                      </Typography>
                      <Chip
                        icon={getStatusIcon(reservation.status)}
                        label={getStatusLabel(reservation.status)}
                        color={getStatusColor(reservation.status)}
                        size="small"
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Stato Pagamento:
                      </Typography>
                      <Chip
                        label={getPaymentStatusLabel(reservation.paymentStatus)}
                        color={getPaymentStatusColor(reservation.paymentStatus)}
                        size="small"
                      />
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h6" color="primary">
                    {reservation.totalPrice}€
                  </Typography>
                </Box>
              </Box>

              {/* User info */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Person sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {reservation.user?.name} {reservation.user?.surname}
                </Typography>
              </Box>

              {/* Date and time info */}
              <Grid container spacing={1}>
                <Grid  size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CalendarToday sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {getDayName(new Date(reservation.date).getDay())} {dayjs(reservation.date).format('DD MMM YY')}
                    </Typography>
                  </Box>
                </Grid>

                <Grid  size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AccessTime sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {reservation.startTime} - {reservation.endTime} ({formatDuration(reservation.duration)})
                    </Typography>
                  </Box>
                </Grid>

                {/* <Grid  size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Schedule sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {formatDuration(reservation.duration)}
                    </Typography>
                  </Box>
                </Grid> */}

                <Grid  size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, minHeight: '2.5em' }}>
                    <LocationOn sx={{ mr: 1, fontSize: 16, color: 'text.secondary', mt: 0.2 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.2,
                      minHeight: '2.4em'
                    }}>
                      {reservation.reservable?.location || (
                        <span style={{ color: 'rgba(0, 0, 0, 0.3)' }}>Non specificata</span>
                      )}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Notes - sempre mostrate per mantenere layout uniforme */}
              <Box sx={{ mt: 2, minHeight: '3.5em' }}>
                <Typography variant="body2" color="text.secondary" sx={{ 
                //   display: '-webkit-box',
                //   WebkitLineClamp: 1.5,
                //   WebkitBoxOrient: 'vertical',
                //   overflow: 'hidden',
                //   lineHeight: 1.5,
                //   minHeight: '3.5em'
                }}>
                  {reservation.notes ? (
                    <>
                      <strong>Note utente:</strong> {reservation.notes}
                    </>
                  ) : (
                    <span style={{ color: 'rgba(0, 0, 0, 0.3)' }}>Nessuna nota</span>
                  )}
                </Typography>
              </Box>

              {/* Admin Notes - sempre mostrate per mantenere layout uniforme */}
              {/* <Box sx={{ mt: 2, minHeight: '4.5em' }}>
                <Typography variant="body2" color="text.secondary" sx={{ 
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: 1.5,
                  minHeight: '4.5em'
                }}>
                  {reservation.adminNotes ? (
                    <>
                      <strong>Note admin:</strong> {reservation.adminNotes}
                    </>
                  ) : (
                    <span style={{ color: 'rgba(0, 0, 0, 0.3)' }}>Nessuna nota admin</span>
                  )}
                </Typography>
              </Box> */}

              <Divider sx={{ my: 2 }} />

              {/* Footer with creation date and actions */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mt: 'auto'
              }}>
                
                <Typography variant="body2" color="text.secondary" style={{ fontStyle: 'italic' }}>
                  Creata il {dayjs(reservation.createdAt).format('DD MMM YY HH:mm')}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {reservation.status === 'pending' && (
                    <>
                      <Tooltip title="Approva">
                        <IconButton 
                          size="small" 
                          color="success"
                          onClick={() => handleApprove(reservation._id)}
                        >
                          <CheckCircle />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Rifiuta">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleReject(reservation._id)}
                        >
                          <Cancel />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}

      {/* Modal di conferma */}
      <ModalConfirm
        open={confirmOpen}
        title={actionType === 'approve' ? 'Conferma Approvazione' : 'Conferma Rifiuto'}
        message={actionType === 'approve' 
          ? 'Sei sicuro di voler approvare questa prenotazione?'
          : 'Sei sicuro di voler rifiutare questa prenotazione? Questa azione non può essere annullata.'
        }
        onConfirm={handleConfirmAction}
        onCancel={handleCancelAction}
      />

      {/* Modal di risultato */}
      <ModalResult
        open={resultOpen}
        onClose={handleResultClose}
        title={resultTitle}
        message={resultMessage}
        severity={resultSeverity}
      />
    </Grid>
  );
};

export default ReservationCards;
